/**
 * Basic Module - JavaScript
 *
 * Handles:
 * - Background video controls (play/pause, mute/unmute)
 * - Supports HTML5, YouTube, and Vimeo videos
 * - Parallax effect for background images
 * ES6 class-based, no dependencies.
 */

export default class BasicModule {
  /**
   * @param {HTMLElement} element - The basic module element (.basic-module)
   */
  constructor(element) {
    // Generate unique ID for this instance
    this.instanceId = `instance-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    this.module = element;
    this.video = element.querySelector('.background-video');
    this.videoWrapper = element.querySelector('.background-video__wrapper');
    this.playPauseBtn = element.querySelector('.video-control__button-play-pause');
    this.muteBtn = element.querySelector('.video-control__button-mute');
    this.parallaxBg = element.querySelector('.parallax-bg');

    // State
    this.isPlaying = false;
    this.isMuted = true;
    this.videoType = null; // 'html5', 'youtube', 'vimeo'
    this.player = null; // YouTube or Vimeo player instance
    this.playerReady = false;
    this.isDestroyed = false; // Flag to prevent operations after destroy

    // Parallax settings
    // 0 = fixed (no parallax, image stays in place like background-attachment: fixed)
    // 0.1-0.3 = subtle parallax
    // 0.5 = moderate parallax (default)
    // 1 = moves with scroll
    this.parallaxSpeed = .1; // Set to 0 for fixed background effect
  }

  /**
   * Initialize the module
   */
  init() {
    console.log(`[BasicModule:${this.instanceId}] Initializing module`, {
      module: this.module,
      hasVideo: !!this.video,
      hasParallax: !!this.parallaxBg,
      hasPlayPauseBtn: !!this.playPauseBtn,
      hasMuteBtn: !!this.muteBtn,
    });

    if (!this.module) return;

    // Detect video type
    if (this.video) {
      this.detectVideoType();
      this.initVideoControls();
      this.checkAutoplay();
    }

    // Initialize parallax if parallax background exists
    if (this.parallaxBg) {
      this.initParallax();
    }
  }

  /**
   * Detect video type (HTML5, YouTube, or Vimeo)
   */
  detectVideoType() {
    if (!this.video) return;

    const videoType = this.video.getAttribute('data-video-type');
    console.log('[BasicModule] Detecting video type:', {
      videoType,
      tagName: this.video.tagName,
      src: this.video.src,
    });

    if (videoType === 'youtube') {
      this.videoType = 'youtube';
      console.log('[BasicModule] Initializing YouTube player');
      this.initYouTubePlayer();
    } else if (videoType === 'vimeo') {
      this.videoType = 'vimeo';
      console.log('[BasicModule] Initializing Vimeo player');
      this.initVimeoPlayer();
    } else if (this.video.tagName === 'VIDEO') {
      this.videoType = 'html5';
      this.playerReady = true;
      console.log('[BasicModule] HTML5 video detected');
    }
  }

  /**
   * Initialize YouTube Player API
   */
  initYouTubePlayer() {
    console.log('[BasicModule] YouTube - Checking API availability', {
      hasYT: !!window.YT,
      hasYTPlayer: !!(window.YT && window.YT.Player),
    });

    // Load YouTube API if not already loaded
    if (!window.YT) {
      console.log('[BasicModule] YouTube - Loading API script');

      // Check if script is already being loaded
      if (!window.youtubeAPILoading) {
        window.youtubeAPILoading = true;
        window.youtubeAPICallbacks = [];

        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

        // Wait for API to be ready
        window.onYouTubeIframeAPIReady = () => {
          console.log('[BasicModule] YouTube - API Ready callback fired');
          window.youtubeAPILoading = false;
          // Execute all waiting callbacks
          window.youtubeAPICallbacks.forEach((callback) => callback());
          window.youtubeAPICallbacks = [];
        };
      }

      // Add this instance to waiting list
      window.youtubeAPICallbacks.push(() => {
        console.log('[BasicModule] YouTube - Executing queued callback for this instance');
        this.createYouTubePlayer();
      });
    } else if (window.YT.Player) {
      console.log('[BasicModule] YouTube - API already loaded, creating player');
      this.createYouTubePlayer();
    } else {
      // YT exists but Player not ready yet
      console.log('[BasicModule] YouTube - YT exists but Player not ready, waiting...');
      setTimeout(() => this.initYouTubePlayer(), 100);
    }
  }

  /**
   * Create YouTube player instance
   */
  createYouTubePlayer() {
    if (!this.video || !window.YT) {
      console.error('[BasicModule] YouTube - Cannot create player:', {
        hasVideo: !!this.video,
        hasYT: !!window.YT,
      });
      return;
    }

    // YouTube API requires iframe to have an ID
    if (!this.video.id) {
      this.video.id = `youtube-player-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      console.log('[BasicModule] YouTube - Added ID to iframe:', this.video.id);
    }

    // Ensure enablejsapi=1 is in the URL
    const currentSrc = this.video.src || '';
    if (currentSrc && !currentSrc.includes('enablejsapi=1')) {
      const separator = currentSrc.includes('?') ? '&' : '?';
      this.video.src = `${currentSrc}${separator}enablejsapi=1`;
      console.log('[BasicModule] YouTube - Added enablejsapi=1 to URL');
    }

    console.log('[BasicModule] YouTube - Creating player instance', {
      iframe: this.video,
      iframeId: this.video.id,
      src: this.video.src,
      hasYTPlayer: !!(window.YT && window.YT.Player),
    });

    try {
      this.player = new window.YT.Player(this.video.id, {
        events: {
          onReady: (event) => {
            console.log('[BasicModule] YouTube - Player ready', event);
            this.playerReady = true;
            this.onYouTubeReady();
          },
          onStateChange: (event) => {
            console.log('[BasicModule] YouTube - State changed:', event.data);
            this.onYouTubeStateChange(event);
          },
          onError: (event) => {
            console.error('[BasicModule] YouTube - Player error:', event.data);
          },
        },
      });
      console.log('[BasicModule] YouTube - Player instance created', this.player);

      // Check if player is already ready (HMR case - iframe was already initialized)
      // Use multiple checks with increasing delays to catch the player when ready
      const checkPlayerReady = (attempt = 1, maxAttempts = 10) => {
        setTimeout(() => {
          // Stop if instance was destroyed
          if (this.isDestroyed) {
            console.log('[BasicModule] YouTube - Instance destroyed, stopping readiness checks');
            return;
          }

          if (this.playerReady) {
            console.log('[BasicModule] YouTube - Player already ready via callback');
            return;
          }

          if (!this.player) {
            console.log('[BasicModule] YouTube - Player instance lost, stopping checks');
            return;
          }

          console.log(`[BasicModule] YouTube - Checking player readiness (attempt ${attempt}/${maxAttempts})`, {
            hasGetPlayerState: typeof this.player.getPlayerState === 'function',
            hasPlayVideo: typeof this.player.playVideo === 'function',
            hasPauseVideo: typeof this.player.pauseVideo === 'function',
          });

          // Check if player methods are available
          if (typeof this.player.getPlayerState === 'function' &&
              typeof this.player.playVideo === 'function' &&
              typeof this.player.pauseVideo === 'function') {
            try {
              // Try to call a safe method to verify player is functional
              const state = this.player.getPlayerState();
              console.log('[BasicModule] YouTube - Player ready detected (HMR case), state:', state);
              this.playerReady = true;
              this.onYouTubeReady();
            } catch (e) {
              console.log('[BasicModule] YouTube - Player methods exist but not functional yet, will retry');
              if (attempt < maxAttempts) {
                checkPlayerReady(attempt + 1, maxAttempts);
              }
            }
          } else if (attempt < maxAttempts) {
            // Methods not available yet, retry
            checkPlayerReady(attempt + 1, maxAttempts);
          } else {
            console.warn('[BasicModule] YouTube - Player readiness check failed after max attempts');
          }
        }, attempt * 100); // Exponential backoff: 100ms, 200ms, 300ms...
      };

      checkPlayerReady();
    } catch (error) {
      console.error('[BasicModule] YouTube - Player initialization failed:', error);
    }
  }

  /**
   * YouTube player ready handler
   */
  onYouTubeReady() {
    // Don't operate on destroyed instances
    if (this.isDestroyed) {
      console.log('[BasicModule] YouTube - onYouTubeReady called on destroyed instance, ignoring');
      return;
    }

    // Check initial mute state from URL
    const isMuted = this.player.isMuted();
    this.isMuted = isMuted;
    this.updateMuteButton();

    // Check if playing
    const state = this.player.getPlayerState();
    this.isPlaying = state === window.YT.PlayerState.PLAYING;
    this.updatePlayPauseButton();
  }

  /**
   * YouTube state change handler
   */
  onYouTubeStateChange(event) {
    // Don't operate on destroyed instances
    if (this.isDestroyed) {
      console.log('[BasicModule] YouTube - onYouTubeStateChange called on destroyed instance, ignoring');
      return;
    }

    if (event.data === window.YT.PlayerState.PLAYING) {
      this.isPlaying = true;
      this.updatePlayPauseButton();
    } else if (event.data === window.YT.PlayerState.PAUSED) {
      this.isPlaying = false;
      this.updatePlayPauseButton();
    }
  }

  /**
   * Initialize Vimeo Player API
   */
  initVimeoPlayer() {
    console.log('[BasicModule] Vimeo - Checking API availability', {
      hasVimeo: !!window.Vimeo,
    });

    // Load Vimeo API if not already loaded
    if (!window.Vimeo) {
      console.log('[BasicModule] Vimeo - Loading API script');

      // Check if script is already being loaded
      if (!window.vimeoAPILoading) {
        window.vimeoAPILoading = true;
        window.vimeoAPICallbacks = [];

        const script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        script.onload = () => {
          console.log('[BasicModule] Vimeo - API script loaded');
          window.vimeoAPILoading = false;
          // Execute all waiting callbacks
          window.vimeoAPICallbacks.forEach((callback) => callback());
          window.vimeoAPICallbacks = [];
        };
        script.onerror = (error) => {
          console.error('[BasicModule] Vimeo - Failed to load API script', error);
          window.vimeoAPILoading = false;
        };
        document.head.appendChild(script);
      }

      // Add this instance to waiting list
      window.vimeoAPICallbacks.push(() => {
        console.log('[BasicModule] Vimeo - Executing queued callback for this instance');
        this.createVimeoPlayer();
      });
    } else {
      console.log('[BasicModule] Vimeo - API already loaded, creating player');
      this.createVimeoPlayer();
    }
  }

  /**
   * Create Vimeo player instance
   */
  createVimeoPlayer() {
    if (!this.video || !window.Vimeo) {
      console.error('[BasicModule] Vimeo - Cannot create player:', {
        hasVideo: !!this.video,
        hasVimeo: !!window.Vimeo,
      });
      return;
    }

    // Add ID to iframe if not present (good practice)
    if (!this.video.id) {
      this.video.id = `vimeo-player-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      console.log('[BasicModule] Vimeo - Added ID to iframe:', this.video.id);
    }

    console.log('[BasicModule] Vimeo - Creating player instance', {
      iframe: this.video,
      iframeId: this.video.id,
      hasVimeoPlayer: !!(window.Vimeo && window.Vimeo.Player),
    });

    try {
      this.player = new window.Vimeo.Player(this.video);
      this.playerReady = true;
      console.log('[BasicModule] Vimeo - Player instance created', this.player);

      // Listen to Vimeo events
      this.player.on('play', () => {
        console.log('[BasicModule] Vimeo - Playing');
        this.isPlaying = true;
        this.updatePlayPauseButton();
      });

      this.player.on('pause', () => {
        console.log('[BasicModule] Vimeo - Paused');
        this.isPlaying = false;
        this.updatePlayPauseButton();
      });

      // Get initial mute state
      this.player.getVolume().then((volume) => {
        console.log('[BasicModule] Vimeo - Initial volume:', volume);
        this.isMuted = volume === 0;
        this.updateMuteButton();
      });
    } catch (error) {
      console.error('[BasicModule] Vimeo - Player initialization failed:', error);
    }
  }

  /**
   * Initialize video controls
   */
  initVideoControls() {
    console.log('[BasicModule] Initializing video controls', {
      videoType: this.videoType,
      hasPlayPauseBtn: !!this.playPauseBtn,
      hasMuteBtn: !!this.muteBtn,
    });

    // Play/Pause button
    if (this.playPauseBtn) {
      this.playPauseBtn.addEventListener('click', () => this.togglePlayPause());
      console.log('[BasicModule] Play/Pause button listener added');
    }

    // Mute/Unmute button
    if (this.muteBtn) {
      this.muteBtn.addEventListener('click', () => this.toggleMute());
      console.log('[BasicModule] Mute/Unmute button listener added');
    }

    // Only add HTML5 video event listeners for HTML5 videos
    // YouTube/Vimeo use their own API callbacks
    if (this.videoType === 'html5' && this.video) {
      console.log('[BasicModule] Adding HTML5 video event listeners');
      this.video.addEventListener('play', () => this.onVideoPlay());
      this.video.addEventListener('pause', () => this.onVideoPause());

      // Set initial state based on video attributes
      this.isMuted = this.video.muted;
      this.updateMuteButton();
      console.log('[BasicModule] HTML5 initial mute state:', this.isMuted);
    } else if (this.videoType === 'youtube' || this.videoType === 'vimeo') {
      // For YouTube/Vimeo, initial state will be set in onYouTubeReady/onVimeoReady
      // Set default muted state from URL params
      const src = this.video?.src || '';
      this.isMuted = src.includes('mute=1') || src.includes('muted=1');
      this.updateMuteButton();
      console.log('[BasicModule] YouTube/Vimeo initial mute state from URL:', this.isMuted);
    }
  }

  /**
   * Check if video should autoplay
   */
  checkAutoplay() {
    if (this.videoType === 'html5') {
      if (this.video.autoplay || this.video.hasAttribute('autoplay')) {
        this.isPlaying = true;
        this.updatePlayPauseButton();
      }
    } else if (this.videoType === 'youtube' || this.videoType === 'vimeo') {
      // For YouTube/Vimeo, autoplay is handled in URL params
      // Check if URL contains autoplay=1
      const src = this.video.src || '';
      if (src.includes('autoplay=1')) {
        this.isPlaying = true;
        this.updatePlayPauseButton();
      }
    }
  }

  /**
   * Toggle play/pause
   */
  togglePlayPause() {
    console.log(`[BasicModule:${this.instanceId}] Toggle play/pause`, {
      isPlaying: this.isPlaying,
      videoType: this.videoType,
      playerReady: this.playerReady,
      hasPlayer: !!this.player,
      isDestroyed: this.isDestroyed,
    });

    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * Play video
   */
  play() {
    console.log('[BasicModule] Play called', {
      hasVideo: !!this.video,
      playerReady: this.playerReady,
      videoType: this.videoType,
      hasPlayer: !!this.player,
    });

    if (!this.video) {
      console.warn('[BasicModule] Play aborted - no video element');
      return;
    }

    // For YouTube/Vimeo, check if player exists and methods are available
    if (this.videoType === 'youtube' && this.player) {
      if (typeof this.player.playVideo === 'function') {
        try {
          console.log('[BasicModule] YouTube - Calling playVideo()');
          this.player.playVideo();
        } catch (error) {
          console.warn('[BasicModule] YouTube - Play failed:', error);
        }
      } else {
        console.warn('[BasicModule] YouTube - Player not ready, playVideo method not available');
      }
    } else if (this.videoType === 'vimeo' && this.player) {
      if (typeof this.player.play === 'function') {
        try {
          console.log('[BasicModule] Vimeo - Calling play()');
          this.player.play();
        } catch (error) {
          console.warn('[BasicModule] Vimeo - Play failed:', error);
        }
      } else {
        console.warn('[BasicModule] Vimeo - Player not ready, play method not available');
      }
    } else if (this.videoType === 'html5') {
      if (!this.playerReady) {
        console.warn('[BasicModule] HTML5 - Play aborted - video not ready');
        return;
      }
      console.log('[BasicModule] HTML5 - Calling play()');
      const playPromise = this.video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('[BasicModule] HTML5 - Play successful');
            this.isPlaying = true;
            this.updatePlayPauseButton();
          })
          .catch((error) => {
            console.warn('[BasicModule] HTML5 - Play failed:', error);
            this.isPlaying = false;
            this.updatePlayPauseButton();
          });
      }
    }
  }

  /**
   * Pause video
   */
  pause() {
    console.log('[BasicModule] Pause called', {
      hasVideo: !!this.video,
      playerReady: this.playerReady,
      videoType: this.videoType,
      hasPlayer: !!this.player,
    });

    if (!this.video) {
      console.warn('[BasicModule] Pause aborted - no video element');
      return;
    }

    // For YouTube/Vimeo, check if player exists and methods are available
    if (this.videoType === 'youtube' && this.player) {
      if (typeof this.player.pauseVideo === 'function') {
        try {
          console.log('[BasicModule] YouTube - Calling pauseVideo()');
          this.player.pauseVideo();
        } catch (error) {
          console.warn('[BasicModule] YouTube - Pause failed:', error);
        }
      } else {
        console.warn('[BasicModule] YouTube - Player not ready, pauseVideo method not available');
      }
    } else if (this.videoType === 'vimeo' && this.player) {
      if (typeof this.player.pause === 'function') {
        try {
          console.log('[BasicModule] Vimeo - Calling pause()');
          this.player.pause();
        } catch (error) {
          console.warn('[BasicModule] Vimeo - Pause failed:', error);
        }
      } else {
        console.warn('[BasicModule] Vimeo - Player not ready, pause method not available');
      }
    } else if (this.videoType === 'html5') {
      if (!this.playerReady) {
        console.warn('[BasicModule] HTML5 - Pause aborted - video not ready');
        return;
      }
      console.log('[BasicModule] HTML5 - Calling pause()');
      this.video.pause();
      this.isPlaying = false;
      this.updatePlayPauseButton();
    }
  }

  /**
   * Toggle mute/unmute
   */
  toggleMute() {
    console.log('[BasicModule] Toggle mute', {
      isMuted: this.isMuted,
      videoType: this.videoType,
      playerReady: this.playerReady,
      hasPlayer: !!this.player,
    });

    if (!this.video || !this.playerReady) {
      console.warn('[BasicModule] Toggle mute aborted - video not ready');
      return;
    }

    if (this.videoType === 'youtube' && this.player) {
      if (this.isMuted) {
        console.log('[BasicModule] YouTube - Calling unMute()');
        this.player.unMute();
        this.isMuted = false;
      } else {
        console.log('[BasicModule] YouTube - Calling mute()');
        this.player.mute();
        this.isMuted = true;
      }
      this.updateMuteButton();
    } else if (this.videoType === 'vimeo' && this.player) {
      if (this.isMuted) {
        console.log('[BasicModule] Vimeo - Setting volume to 1');
        this.player.setVolume(1);
        this.isMuted = false;
      } else {
        console.log('[BasicModule] Vimeo - Setting volume to 0');
        this.player.setVolume(0);
        this.isMuted = true;
      }
      this.updateMuteButton();
    } else if (this.videoType === 'html5') {
      console.log('[BasicModule] HTML5 - Toggle muted property');
      this.video.muted = !this.video.muted;
      this.isMuted = this.video.muted;
      this.updateMuteButton();
    }
  }

  /**
   * Update play/pause button state
   */
  updatePlayPauseButton() {
    if (!this.playPauseBtn) return;

    console.log('[BasicModule] Updating play/pause button', {
      isPlaying: this.isPlaying,
    });

    if (this.isPlaying) {
      this.playPauseBtn.classList.add('is-playing');
      this.playPauseBtn.setAttribute('aria-label',
        this.playPauseBtn.querySelector('.video-control__button-pause-label')?.textContent || 'Pause Video',
      );
    } else {
      this.playPauseBtn.classList.remove('is-playing');
      this.playPauseBtn.setAttribute('aria-label',
        this.playPauseBtn.querySelector('.video-control__button-play-label')?.textContent || 'Play Video',
      );
    }
  }

  /**
   * Update mute button state
   */
  updateMuteButton() {
    if (!this.muteBtn) return;

    console.log('[BasicModule] Updating mute button', {
      isMuted: this.isMuted,
    });

    if (this.isMuted) {
      this.muteBtn.classList.add('is-muted');
      this.muteBtn.setAttribute('aria-label',
        this.muteBtn.querySelector('.video-control__button-unmute-label')?.textContent || 'Unmute Video',
      );
    } else {
      this.muteBtn.classList.remove('is-muted');
      this.muteBtn.setAttribute('aria-label',
        this.muteBtn.querySelector('.video-control__button-mute-label')?.textContent || 'Mute Video',
      );
    }
  }

  /**
   * Video play event handler
   */
  onVideoPlay() {
    this.isPlaying = true;
    this.updatePlayPauseButton();
  }

  /**
   * Video pause event handler
   */
  onVideoPause() {
    this.isPlaying = false;
    this.updatePlayPauseButton();
  }

  /**
   * Initialize parallax effect
   */
  initParallax() {
    // Use Intersection Observer to only apply parallax when in viewport
    this.observeParallax();

    // Add scroll listener
    this.handleParallaxScroll = this.handleParallaxScroll.bind(this);
    window.addEventListener('scroll', this.handleParallaxScroll, { passive: true });
  }

  /**
   * Observe parallax element with Intersection Observer
   */
  observeParallax() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.parallaxActive = true;
            this.handleParallaxScroll(); // Initial position
          } else {
            this.parallaxActive = false;
          }
        });
      },
      {
        rootMargin: '100px 0px', // Start slightly before element enters viewport
      },
    );

    observer.observe(this.module);
  }

  /**
   * Handle parallax scroll
   */
  handleParallaxScroll() {
    if (!this.parallaxActive || !this.parallaxBg) return;

    // Get element position
    const rect = this.module.getBoundingClientRect();
    const scrolled = window.pageYOffset;
    const elementTop = rect.top + scrolled;
    const windowHeight = window.innerHeight;

    // Calculate parallax offset
    // Only apply when element is in/near viewport
    if (rect.top < windowHeight && rect.bottom > 0) {
      // For fixed background effect (parallaxSpeed = 0), keep image centered
      // For parallax effect (parallaxSpeed > 0), move slower than scroll
      const offset = (scrolled - elementTop + windowHeight) * this.parallaxSpeed;

      // Apply transform
      this.parallaxBg.style.transform = `translate3d(0, ${offset}px, 0)`;
    }
  }

  /**
   * Destroy instance and cleanup
   */
  destroy() {
    console.log(`[BasicModule:${this.instanceId}] Destroying instance`);

    // Mark as destroyed to stop any pending async operations
    this.isDestroyed = true;

    // Cleanup YouTube/Vimeo player
    if (this.player) {
      try {
        if (this.videoType === 'youtube' && typeof this.player.destroy === 'function') {
          this.player.destroy();
        } else if (this.videoType === 'vimeo' && typeof this.player.destroy === 'function') {
          this.player.destroy();
        }
      } catch (error) {
        console.warn('[BasicModule] Error destroying player:', error);
      }
      this.player = null;
    }

    // Remove video event listeners
    if (this.video) {
      this.video.removeEventListener('play', () => this.onVideoPlay());
      this.video.removeEventListener('pause', () => this.onVideoPause());
    }

    // Remove parallax scroll listener
    if (this.handleParallaxScroll) {
      window.removeEventListener('scroll', this.handleParallaxScroll);
    }

    // Pause video if playing
    if (this.isPlaying && this.video && this.videoType === 'html5') {
      this.video.pause();
    }

    // Reset state
    this.playerReady = false;
    this.isPlaying = false;
  }
}

// Global map to store instances across HMR reloads
// Use regular Map with stable keys (not WeakMap with element references)
if (!window.__basicModuleInstances) {
  window.__basicModuleInstances = new Map();
}

// Generate a stable key for an element that survives HMR
function getElementKey(element, index) {
  // Always generate key from element's ID or its position
  // Don't use dataset as it's lost when DOM is recreated
  return element.id || `module-${index}`;
}

// Global storage for initial HTML (before module init)
if (!window.__STORYBOOK_INITIAL_HTML__) {
  window.__STORYBOOK_INITIAL_HTML__ = new Map();
}

// Auto-initialize all basic modules on page
function initBasicModules() {
  const modules = document.querySelectorAll('.basic-module');
  console.log('[BasicModule] Auto-init - Found modules:', modules.length);

  modules.forEach((moduleElement, index) => {
    // Get stable key for this element
    const elementKey = getElementKey(moduleElement, index);

    // Check if instance already exists in global map
    const existingInstance = window.__basicModuleInstances.get(elementKey);
    if (existingInstance && !existingInstance.isDestroyed) {
      console.log(`[BasicModule:${existingInstance.instanceId}] Auto-init - Module ${index} (${elementKey}) still active, skipping re-init`);
      // Re-attach instance to element for easy access
      moduleElement.basicModuleInstance = existingInstance;
      return;
    }

    // Clean up old instance if exists and destroyed
    if (existingInstance && existingInstance.isDestroyed) {
      console.log(`[BasicModule:${existingInstance.instanceId}] Auto-init - Old instance destroyed, re-initializing module ${index}`);
      window.__basicModuleInstances.delete(elementKey);
    }

    // ===== CAPTURE INITIAL HTML BEFORE MODULE INIT =====
    // This HTML snapshot is used by Storybook Export addon to get clean HTML
    // without runtime classes/attributes added by module scripts
    if (!window.__STORYBOOK_INITIAL_HTML__.has(elementKey)) {
      console.log(`[BasicModule] Capturing initial HTML for module ${index} (${elementKey})`);
      // Deep clone to preserve original state
      const initialClone = moduleElement.cloneNode(true);
      window.__STORYBOOK_INITIAL_HTML__.set(elementKey, initialClone);
    }
    // ===== END CAPTURE =====

    console.log(`[BasicModule] Auto-init - Initializing module ${index} (${elementKey})`);
    const instance = new BasicModule(moduleElement);
    instance.init();

    // Store instance in global map with stable key (survives HMR)
    window.__basicModuleInstances.set(elementKey, instance);

    // Also store on element for easy access
    moduleElement.basicModuleInstance = instance;
  });
}
